import React from 'react';
import { useLocale } from 'client/i18n/hooks/useLocale';
import { useFetchSpotifyAccount } from 'client/hooks/spotifyConnect/useFetchSpotifyAccount';
import { useLocation } from 'react-router-dom';
import { getScreenFromRoute } from 'client/modules/analytics';
import AnchorErrorTracking from 'client/modules/AnchorErrorTracking';
import {
  SPOTIFY_OAUTH_SUCCESS_MESSAGE,
  initiateOAuth,
  SPOTIFY_OAUTH_FAIL_MESSAGE,
} from './spotifyConnectUtils';
import { SpotifyConnectSuccessModal } from './SpotifyConnectSuccessModal';
import { SpotifyConnectInitialModal } from './SpotifyConnectInitialModal';
import { SpotifyConnectErrorModal } from './SpotifyConnectErrorModal';
import { SpotifyConnectComponent, events } from '../events';

type SpotifyConnectModalProps = {
  onComplete: (state: SpotifyConnectState) => void;
  component: SpotifyConnectComponent;
};

export type SpotifyConnectState =
  | 'initial'
  | 'success'
  | 'failure'
  | 'cancelled';

const SENTRY_FEATURE_NAME = 'Spotify Connect';

export const SpotifyConnectModal = ({
  onComplete,
  component,
}: SpotifyConnectModalProps) => {
  const { locale } = useLocale();
  const [
    spotifyConnectState,
    setSpotifyConnectState,
  ] = React.useState<SpotifyConnectState>('initial');
  const {
    data: accountInfo,
    status: spotifyAccountStatus,
    refetch,
  } = useFetchSpotifyAccount({
    enabled: false,
  });

  const { pathname } = useLocation();
  const location = getScreenFromRoute(pathname);

  const onConnectedToSpotify = (e: MessageEvent) => {
    if (e.data === SPOTIFY_OAUTH_SUCCESS_MESSAGE) {
      events.spotifyConnectedOauthSuccess(component);
      refetch();
      // TODO: handle failure and error response codes
      setSpotifyConnectState('success');
    } else if (e.data === SPOTIFY_OAUTH_FAIL_MESSAGE) {
      events.spotifyConnectedOauthFailure(component);
      setSpotifyConnectState('failure');
    }
  };

  switch (spotifyConnectState) {
    case 'initial':
      return (
        <SpotifyConnectInitialModal
          onConnectSpotify={() => {
            events.connectToSpotifyConfirmationClicked(component);
            initiateOAuth({ locale, onSuccess: onConnectedToSpotify });
          }}
          onClose={() => onComplete('cancelled')}
          component={component}
          location={location}
        />
      );
    case 'success':
      return (
        <SpotifyConnectSuccessModal
          accountInfo={accountInfo}
          onContinue={() => onComplete('success')}
          onClose={() => onComplete('success')}
          isLoading={spotifyAccountStatus === 'loading'}
          component={component}
          location={location}
        />
      );
    case 'failure':
      AnchorErrorTracking.captureException({
        error: new Error(
          `Spotify Connect Modal: Spotify Connect OAuth Failure`
        ),
        extraInformation: { spotifyAccountStatus, spotifyConnectState },
        tags: {
          feature: SENTRY_FEATURE_NAME,
          component: SpotifyConnectModal.name,
        },
      });

      return (
        <SpotifyConnectErrorModal
          onClose={() => onComplete('cancelled')}
          onRetry={() => setSpotifyConnectState('initial')}
          component={component}
          location={location}
        />
      );
    default:
      AnchorErrorTracking.captureException({
        error: new Error(
          `Spotify Connect Modal: unexpected spotifyConnectState`
        ),
        extraInformation: { spotifyAccountStatus, spotifyConnectState },
        tags: {
          feature: SENTRY_FEATURE_NAME,
          component: SpotifyConnectModal.name,
        },
      });

      return null; // should never happen
  }
};
